import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

export interface DataTableBulkBarProps {
  count: number;
  onClear: () => void;
  children?: ReactNode;
}

/**
 * Floating bar that rises when rows are selected.
 *
 * It stays mounted and hidden so the transform can animate both ways, and it is
 * `aria-live` because the selection count is the thing a bulk action depends on.
 *
 * `inert` while hidden is not decoration: transparency and `pointer-events`
 * stop the mouse and nothing else, so five invisible buttons stayed in the tab
 * order after the table and answered the keyboard — an `aria-hidden` subtree
 * with focusable children, which is the axe rule `aria-hidden-focus`. `inert`
 * is the one thing that removes a visible-but-inactive branch from focus, the
 * accessibility tree and keyboard activation at once.
 */
export function DataTableBulkBar({ count, onClear, children }: DataTableBulkBarProps) {
  const active = count > 0;

  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-x-0 bottom-4 z-40 flex justify-center px-4 transition-all duration-150 ease-out',
        active ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0',
      )}
      aria-hidden={!active}
      inert={!active}
    >
      <div
        className={cn(
          'flex items-center gap-2 rounded-lg border border-border bg-background px-2 py-1.5 shadow-lg',
          active && 'pointer-events-auto',
        )}
      >
        <span className="px-1.5 text-sm tabular" aria-live="polite">
          {count} {count === 1 ? 'seleccionada' : 'seleccionadas'}
        </span>
        <Separator orientation="vertical" className="h-5" />
        {children}
        <Separator orientation="vertical" className="h-5" />
        <Button variant="ghost" size="icon-sm" aria-label="Limpiar selección" onClick={onClear}>
          <X className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}
